import React, { useState, useEffect } from 'react';
import './ManageBill.css';

const API_BASE_URL = 'https://doticom.vn:3000/api/bills';
const UPDATE_TAX_INFO_URL = API_BASE_URL.replace(/\/api\/bills\/?$/, '/api/updateTaxInfo');
// Same production BE as the other bill APIs.
const SYNC_TAX_URL = API_BASE_URL.replace(/\/api\/bills\/?$/, '/sync-tax');
const TOKEN = 'fkawnfgjunqewjfn23784febfqwef';

// Tổng bill sau giảm % và giảm tiền mặt, làm tròn đến đồng (khớp cách BE tính)
const calcBillTotal = (tienHang, giamPT, giamTM) => Math.max(
  0,
  Math.round(Number(tienHang || 0) * (1 - Number(giamPT || 0) / 100) - Number(giamTM || 0))
);

const ManageBill = () => {
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedBill, setSelectedBill] = useState(null);
  const [billForm, setBillForm] = useState({ MaHTTT: '', GhiChu: '', GiamPT: '' });
  const [savingBill, setSavingBill] = useState(false);
  const isAdmin = localStorage.getItem('isAdmin') === 'true';
  const [taxSyncStatuses, setTaxSyncStatuses] = useState({});
  const taxSyncStatus = taxSyncStatuses[selectedBill?.IDXuat] || 'idle';
  const [showModal, setShowModal] = useState(false);
  const [showItemModal, setShowItemModal] = useState(false);
  const [itemForm, setItemForm] = useState({});
  const [editingItem, setEditingItem] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [stores, setStores] = useState([]);
  const [storeSearchTerm, setStoreSearchTerm] = useState('');
  const [showStoreDropdown, setShowStoreDropdown] = useState(false);
  const [selectedStore, setSelectedStore] = useState(null);
  const [isBakery, setIsBakery] = useState(false);
  const [soPxSearchInput, setSoPxSearchInput] = useState('');
  
  // Filters
  const [filters, setFilters] = useState({
    MaCH: localStorage.getItem('IDCH') || '',
    TuNgay: new Date().toISOString().split('T')[0],
    DenNgay: new Date().toISOString().split('T')[0],
    page: 1,
    pageSize: 50
  });

  const [toast, setToast] = useState({ show: false, message: '', type: '' });
  const [apiMessageModal, setApiMessageModal] = useState({
    show: false,
    message: ''
  });

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: '' }), 3000);
  };

  const showApiMessage = (message) => {
    setApiMessageModal({ show: true, message });
  };

  const closeApiMessage = () => {
    setApiMessageModal({ show: false, message: '' });
  };

  const getEditedBy = () => (
    localStorage.getItem('TenUser') ||
    localStorage.getItem('userId') ||
    'Admin'
  );

  const getAdminRolePayload = () => (
    localStorage.getItem('isAdmin') === 'true'
      ? { isAdmin: true }
      : {}
  );

  const getBillWriteHeaders = () => {
    const userToken = localStorage.getItem('token');
    return {
      'Authorization': TOKEN,
      'Content-Type': 'application/json',
      ...(userToken ? { 'X-User-Token': userToken } : {})
    };
  };

  const requireAdminAction = () => {
    if (localStorage.getItem('isAdmin') === 'true') return true;
    showApiMessage('Chỉ admin được thêm, sửa, xóa sản phẩm hoặc xóa bill');
    return false;
  };

  // Helper function to remove Vietnamese diacritics
  const removeDiacritics = (str) => {
    return str
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
  };

  // Filter stores based on search term
  const getFilteredStores = () => {
    if (!storeSearchTerm.trim()) return stores;
    
    const searchLower = removeDiacritics(storeSearchTerm);
    return stores.filter(store => {
      const tenCH = removeDiacritics(store.TenCuaHang || '');
      const maBravo = removeDiacritics(store.MaBravo || '');
      const idch = store.IDCH?.toString() || '';
      
      return tenCH.includes(searchLower) || 
             maBravo.includes(searchLower) || 
             idch.includes(searchLower);
    });
  };

  const selectStore = (store) => {
    setSelectedStore(store);
    setFilters({ ...filters, MaCH: store.IDCH.toString() });
    setStoreSearchTerm(store.TenCuaHang);
    setShowStoreDropdown(false);
  };

  const fetchBills = async () => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams(filters).toString();
      const response = await fetch(`${API_BASE_URL}?${queryParams}`, {
        headers: { 'Authorization': TOKEN }
      });
      const result = await response.json();
      
      if (result.success) {
        setBills(result.data || []);
        setPagination(result.pagination);
      } else {
        showApiMessage(result.message || 'Lỗi khi tải dữ liệu');
      }
    } catch (error) {
      showToast('Lỗi kết nối server', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchBillDetail = async (billId, { resetForm = true } = {}) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/${billId}`, {
        headers: { 'Authorization': TOKEN }
      });
      const result = await response.json();
      
      if (result.success) {
        // Map ChiTiet to items for consistency
        const items = result.data.ChiTiet || [];
        const calculatedTotal = items.reduce((sum, item) => sum + (item.ThanhTien || 0), 0);
        
        const billData = {
          ...result.data,
          items: items,
          SoCT: result.data.IDXuat,
          NgayCT: result.data.NgayXuat,
          TienHang: calculatedTotal,
          TongTien: calcBillTotal(calculatedTotal, result.data.GiamPT, result.data.GiamTM)
        };
        setSelectedBill(billData);
        if (resetForm) {
          setBillForm({
            MaHTTT: String(result.data.MaHTTT ?? ''),
            GhiChu: result.data.GhiChu || '',
            GiamPT: String(Number(result.data.GiamPT || 0))
          });
        }
        setShowModal(true);
      } else {
        showApiMessage(result.message || 'Không tìm thấy bill');
      }
    } catch (error) {
      showToast('Lỗi khi tải chi tiết bill', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSyncTax = async () => {
    if (localStorage.getItem('isAdmin') !== 'true' || !selectedBill?.IDXuat || taxSyncStatus === 'loading') return;

    const billId = selectedBill.IDXuat;
    setTaxSyncStatuses(previous => ({ ...previous, [billId]: 'loading' }));

    try {
      const response = await fetch(SYNC_TAX_URL, {
        method: 'POST',
        headers: getBillWriteHeaders(),
        body: JSON.stringify({ IDXuat: billId })
      });
      const result = await response.json();

      if (!response.ok || result?.success !== true) {
        throw new Error(result?.message || 'Đồng bộ thất bại');
      }

      setTaxSyncStatuses(previous => ({ ...previous, [billId]: 'success' }));
    } catch (error) {
      setTaxSyncStatuses(previous => ({ ...previous, [billId]: 'error' }));
    }
  };

  const updateActTWithTaxInfo = async (billId, actT) => {
    const response = await fetch(UPDATE_TAX_INFO_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        TaxInfo: '',
        Act_T: actT,
        IDXuat: billId
      })
    });
    const result = await response.json();

    if (!response.ok || !result.success) {
      throw new Error(result.message || `Không thể cập nhật Act_T=${actT}`);
    }

    return result;
  };

  const updateBill = async (billId, updateData, actTAfterUpdate = null) => {
    let billUpdated = false;

    try {
      const response = await fetch(`${API_BASE_URL}/${billId}`, {
        method: 'PUT',
        headers: getBillWriteHeaders(),
        body: JSON.stringify({
          ...updateData,
          EditedBy: getEditedBy(),
          ...getAdminRolePayload()
        })
      });
      const result = await response.json();
      
      if (result.success) {
        billUpdated = true;

        if (actTAfterUpdate !== null) {
          await updateActTWithTaxInfo(billId, actTAfterUpdate);
        }

        showToast('Cập nhật bill thành công', 'success');
        await fetchBills();
        await fetchBillDetail(billId, { resetForm: updateData.GhiChu !== undefined || updateData.MaHTTT !== undefined || updateData.GiamPT !== undefined });
        return true;
      } else {
        showApiMessage(result.message || 'Lỗi khi cập nhật bill');
      }
    } catch (error) {
      const message = billUpdated
        ? `Đã cập nhật bill nhưng cập nhật Act_T thất bại: ${error.message}`
        : 'Lỗi khi cập nhật bill';
      if (billUpdated) {
        showApiMessage(message);
      } else {
        showToast(message, 'error');
      }

      if (billUpdated) {
        await fetchBills();
        await fetchBillDetail(billId, { resetForm: updateData.GhiChu !== undefined || updateData.MaHTTT !== undefined || updateData.GiamPT !== undefined });
      }
    }

    return false;
  };

  const handlePaymentMethodChange = (event) => {
    setBillForm({ ...billForm, MaHTTT: event.target.value });
  };

  const isGiamPTChanged = Boolean(
    selectedBill &&
    isAdmin &&
    billForm.GiamPT !== '' &&
    Number(billForm.GiamPT) !== Number(selectedBill.GiamPT || 0)
  );

  const hasBillChanges = selectedBill && (
    billForm.MaHTTT !== String(selectedBill.MaHTTT ?? '') ||
    billForm.GhiChu !== (selectedBill.GhiChu || '') ||
    isGiamPTChanged
  );

  const handleUpdateBill = async () => {
    if (!selectedBill || savingBill || !hasBillChanges) return;

    const updateData = {};
    if (billForm.MaHTTT !== String(selectedBill.MaHTTT ?? '')) {
      updateData.MaHTTT = billForm.MaHTTT;
    }
    if (billForm.GhiChu !== (selectedBill.GhiChu || '')) {
      updateData.GhiChu = billForm.GhiChu;
    }
    if (isGiamPTChanged) {
      const giamPT = Number(billForm.GiamPT);
      if (!Number.isFinite(giamPT) || giamPT < 0 || giamPT > 100) {
        showApiMessage('% giảm giá phải là số từ 0 đến 100');
        return;
      }
      const newTotal = calcBillTotal(selectedBill.TienHang, giamPT, selectedBill.GiamTM);
      const taxIssuedNote = Number(selectedBill.Act_T) === 1
        ? '\nLưu ý: bill đã xuất hóa đơn thuế, cần điều chỉnh HĐĐT tương ứng.'
        : '';
      if (!window.confirm(`Áp dụng giảm ${giamPT}% cho bill ${selectedBill.MaPX}?\nTổng tiền mới: ${formatCurrency(newTotal)}${taxIssuedNote}`)) return;
      updateData.GiamPT = giamPT;
    }
    const shouldMarkTaxUpdateFailed =
      updateData.MaHTTT !== undefined &&
      Number(selectedBill.MaHTTT) === 0 &&
      Number(selectedBill.Act_T) === 0 &&
      Number(updateData.MaHTTT) !== 0;

    setSavingBill(true);
    try {
      await updateBill(selectedBill.IDXuat, updateData, shouldMarkTaxUpdateFailed ? 2 : null);
    } finally {
      setSavingBill(false);
    }
  };

  const deleteBill = async (billId) => {
    if (!requireAdminAction()) return;
    if (!window.confirm('Bạn có chắc chắn muốn xóa bill này?')) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/${billId}`, {
        method: 'DELETE',
        headers: getBillWriteHeaders(),
        body: JSON.stringify({
          EditedBy: getEditedBy(),
          ...getAdminRolePayload()
        })
      });
      const result = await response.json();
      
      if (result.success) {
        showToast('Xóa bill thành công', 'success');
        setShowModal(false);
        fetchBills();
      } else {
        showApiMessage(result.message || 'Lỗi khi xóa bill');
      }
    } catch (error) {
      showToast('Lỗi khi xóa bill', 'error');
    }
  };

  const addItemToBill = async () => {
    if (!requireAdminAction()) return;
    if (!selectedBill) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/${selectedBill.IDXuat}/items`, {
        method: 'POST',
        headers: getBillWriteHeaders(),
        body: JSON.stringify({
          ...itemForm,
          EditedBy: getEditedBy(),
          ...getAdminRolePayload()
        })
      });
      const result = await response.json();
      
      if (result.success) {
        showToast('Thêm sản phẩm thành công', 'success');
        setShowItemModal(false);
        setItemForm({});
        await fetchBillDetail(selectedBill.IDXuat, { resetForm: false });
        
        // Cập nhật tổng tiền
        const newTotal = calcBillTotal(
          Number(selectedBill.TienHang || 0) + Number(itemForm.ThanhTien || 0),
          selectedBill.GiamPT,
          selectedBill.GiamTM
        );
        await updateBill(selectedBill.IDXuat, { TongTien: newTotal });
      } else {
        showApiMessage(result.message || 'Lỗi khi thêm sản phẩm');
      }
    } catch (error) {
      showToast('Lỗi khi thêm sản phẩm', 'error');
    }
  };

  const updateBillItem = async (itemId) => {
    if (!requireAdminAction()) return;
    if (!selectedBill) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/${selectedBill.IDXuat}/items/${itemId}`, {
        method: 'PUT',
        headers: getBillWriteHeaders(),
        body: JSON.stringify({
          ...itemForm,
          EditedBy: getEditedBy(),
          ...getAdminRolePayload()
        })
      });
      const result = await response.json();
      
      if (result.success) {
        showToast('Cập nhật sản phẩm thành công', 'success');
        setShowItemModal(false);
        setItemForm({});
        setEditingItem(null);
        await fetchBillDetail(selectedBill.IDXuat, { resetForm: false });
        
        // Tính lại tổng tiền từ tất cả items
        const updatedItems = selectedBill.items.map(item => 
          item.IDXuatCT === itemId ? { ...item, ...itemForm } : item
        );
        const newTotal = calcBillTotal(
          updatedItems.reduce((sum, item) => sum + Number(item.ThanhTien || 0), 0),
          selectedBill.GiamPT,
          selectedBill.GiamTM
        );
        await updateBill(selectedBill.IDXuat, { TongTien: newTotal });
      } else {
        showApiMessage(result.message || 'Lỗi khi cập nhật sản phẩm');
      }
    } catch (error) {
      showToast('Lỗi khi cập nhật sản phẩm', 'error');
    }
  };

  const deleteItem = async (itemId) => {
    if (!requireAdminAction()) return;
    if (!window.confirm('Bạn có chắc chắn muốn xóa sản phẩm này?')) return;
    
    try {
      const itemToDelete = selectedBill.items.find(item => item.IDXuatCT === itemId);
      
      const response = await fetch(`${API_BASE_URL}/${selectedBill.IDXuat}/items/${itemId}`, {
        method: 'DELETE',
        headers: getBillWriteHeaders(),
        body: JSON.stringify({
          EditedBy: getEditedBy(),
          ...getAdminRolePayload()
        })
      });
      const result = await response.json();
      
      if (result.success) {
        showToast('Xóa sản phẩm thành công', 'success');
        await fetchBillDetail(selectedBill.IDXuat, { resetForm: false });
        
        // Cập nhật tổng tiền sau khi xóa
        const newTotal = calcBillTotal(
          Number(selectedBill.TienHang || 0) - Number(itemToDelete?.ThanhTien || 0),
          selectedBill.GiamPT,
          selectedBill.GiamTM
        );
        await updateBill(selectedBill.IDXuat, { TongTien: newTotal });
      } else {
        showApiMessage(result.message || 'Lỗi khi xóa sản phẩm');
      }
    } catch (error) {
      showToast('Lỗi khi xóa sản phẩm', 'error');
    }
  };

  const openEditItemModal = (item) => {
    if (!requireAdminAction()) return;
    setEditingItem(item);
    setItemForm({
      SoLuong: item.SoLuong,
      GiaBan: item.GiaBan,
      ThanhTien: item.ThanhTien
    });
    setShowItemModal(true);
  };

  const searchProducts = async (term) => {
    if (!term || term.trim().length < 2) {
      setSearchResults([]);
      return;
    }

    setSearchLoading(true);
    try {
      const loaiCH = localStorage.getItem('LoaiCH');
      const isHcmAdmin = localStorage.getItem('HCM_ADMIN') === 'true';
      const searchEndpoint = 'https://doticom.vn:3000/items/search-hcm'
      const idch = selectedBill?.MaCH || selectedStore?.IDCH || filters.MaCH || localStorage.getItem('IDCH') || '';
      const searchParams = new URLSearchParams({
        searchTerm: term.trim(),
        IDCH: idch.toString()
      }).toString();

      const response = await fetch(
        `${searchEndpoint}?${searchParams}`,
        {
          headers: { 'Authorization': TOKEN }
        }
      );
      const result = await response.json();
      setSearchResults(result || []);
    } catch (error) {
      showToast('Lỗi khi tìm kiếm sản phẩm', 'error');
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const selectProduct = (product) => {
    setItemForm({
      MaSP: product.MaSP,
      TenSP: product.Ten_SP,
      SoLuong: 1,
      GiaBan: product.DonGia,
      ThanhTien: product.DonGia,
      DVT_XCT: product.DVT_SP,
      TenUser: localStorage.getItem('TenUser') || 'Admin',
      MaCH_CT: selectedBill?.MaCH || parseInt(filters.MaCH.split(',')[0]),
      ThoiGianChon: selectedBill?.NgayXuat || new Date().toISOString()
    });
    setSearchTerm('');
    setSearchResults([]);
  };

  const openAddItemModal = () => {
    if (!requireAdminAction()) return;
    setEditingItem(null);
    setItemForm({
      MaSP: '',
      TenSP: '',
      SoLuong: 1,
      GiaBan: 0,
      ThanhTien: 0,
      DVT_XCT: '',
      TenUser: localStorage.getItem('userId') || 'Admin',
      MaCH_CT: selectedBill?.MaCH || parseInt(filters.MaCH.split(',')[0]),
      ThoiGianChon: selectedBill?.NgayXuat || new Date().toISOString()
    });
    setSearchTerm('');
    setSearchResults([]);
    setShowItemModal(true);
  };
 
  const handlePageChange = (newPage) => {
    setFilters({ ...filters, page: newPage });
  };

  const handlePageSizeChange = (newPageSize) => {
    setFilters({ ...filters, pageSize: newPageSize, page: 1 });
  };

  const fetchStores = async () => {
    try {
      const isAdmin = localStorage.getItem('isAdmin') === 'true';
      const isHcmAdmin = localStorage.getItem('HCM_ADMIN') === 'true';
      const isBakeryUser = localStorage.getItem('isBakery') === 'true';
      
      setIsBakery(isBakeryUser);
      
      // If bakery user, set fixed store and return
      if (isBakeryUser) {
        const idch = localStorage.getItem('IDCH');
        const tenCH = localStorage.getItem('TenCuaHang');
        if (idch && tenCH) {
          setSelectedStore({ IDCH: parseInt(idch), TenCuaHang: tenCH });
          setStoreSearchTerm(tenCH);
          setFilters({ ...filters, MaCH: idch });
        }
        return;
      }
      
      let endpoint = '';
      if (isAdmin) {
        endpoint = 'https://doticom.vn:3000/stores';
      } else if (isHcmAdmin) {
        endpoint = 'https://doticom.vn:3000/stores-hcm';
      }
      
      if (endpoint) {
        const response = await fetch(endpoint, {
          headers: { 'Authorization': TOKEN }
        });
        const result = await response.json();
        
        if (Array.isArray(result)) {
          setStores(result);
          // Set initial selected store if IDCH exists
          const idch = localStorage.getItem('IDCH');
          if (idch) {
            const store = result.find(s => s.IDCH.toString() === idch);
            if (store) {
              setSelectedStore(store);
              setStoreSearchTerm(store.TenCuaHang);
            }
          }
        } else if (result.success && Array.isArray(result.data)) {
          setStores(result.data);
          const idch = localStorage.getItem('IDCH');
          if (idch) {
            const store = result.data.find(s => s.IDCH.toString() === idch);
            if (store) {
              setSelectedStore(store);
              setStoreSearchTerm(store.TenCuaHang);
            }
          }
        }
      }
    } catch (error) {
      console.error('Lỗi khi tải danh sách cửa hàng:', error);
    }
  };

  useEffect(() => {
    fetchStores();
  }, []);

  useEffect(() => {
    fetchBills();
  }, [filters]);

  const handleSoPXSearch = async () => {
    if (!soPxSearchInput.trim()) {
      showToast('Vui lòng nhập số phiếu', 'error');
      return;
    }

    setLoading(true);
    try {
      const searchParams = new URLSearchParams({
        TuNgay: filters.TuNgay,
        DenNgay: filters.DenNgay,
        MaCH: (selectedStore?.IDCH?.toString() || filters.MaCH || '').trim(),
        SoPX: soPxSearchInput.trim(),
        page: '1',
        pageSize: '20'
      }).toString();

      const response = await fetch(`https://doticom.vn:3000/api/bills/search?${searchParams}`, {
        headers: { 'Authorization': TOKEN }
      });
      const result = await response.json();

      if (result.success) {
        setBills(result.data || []);
        setPagination(result.pagination || null);
      } else {
        setBills([]);
        setPagination(null);
        showApiMessage(result.message || 'Không tìm thấy dữ liệu theo số phiếu');
      }
    } catch (error) {
      showToast('Lỗi khi tìm kiếm theo số phiếu', 'error');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(value);
  };

  const PaginationControls = () => {
    if (!pagination) return null;

    const { currentPage, totalPages, hasNextPage, hasPreviousPage, totalRecords } = pagination;
    const pageNumbers = [];
    
    // Tính toán các trang hiển thị
    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, currentPage + 2);
    
    if (currentPage <= 3) {
      endPage = Math.min(5, totalPages);
    }
    if (currentPage > totalPages - 3) {
      startPage = Math.max(1, totalPages - 4);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return (
      <div className="pagination-container">
        <div className="pagination-info">
          Hiển thị {bills.length} / {totalRecords} kết quả
        </div>
        
        <div className="pagination-controls">
          <button
            className="pagination-btn"
            onClick={() => handlePageChange(1)}
            disabled={!hasPreviousPage}
          >
            ««
          </button>
          
          <button
            className="pagination-btn"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={!hasPreviousPage}
          >
            «
          </button>

          {startPage > 1 && (
            <>
              <button className="pagination-btn" onClick={() => handlePageChange(1)}>
                1
              </button>
              {startPage > 2 && <span className="pagination-dots">...</span>}
            </>
          )}

          {pageNumbers.map(num => (
            <button
              key={num}
              className={`pagination-btn ${currentPage === num ? 'active' : ''}`}
              onClick={() => handlePageChange(num)}
            >
              {num}
            </button>
          ))}

          {endPage < totalPages && (
            <>
              {endPage < totalPages - 1 && <span className="pagination-dots">...</span>}
              <button className="pagination-btn" onClick={() => handlePageChange(totalPages)}>
                {totalPages}
              </button>
            </>
          )}

          <button
            className="pagination-btn"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={!hasNextPage}
          >
            »
          </button>
          
          <button
            className="pagination-btn"
            onClick={() => handlePageChange(totalPages)}
            disabled={!hasNextPage}
          >
            »»
          </button>
        </div>

        <div className="page-size-selector">
          <label>Số dòng:</label>
          <select 
            value={filters.pageSize} 
            onChange={(e) => handlePageSizeChange(parseInt(e.target.value))}
          >
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
        </div>
      </div>
    );
  };

  return (
    <div className="manage-bill-container">
      {toast.show && (
        <div className={`toast toast-${toast.type}`}>
          {toast.message}
        </div>
      )}

      {apiMessageModal.show && (
        <div className="api-message-overlay" onClick={closeApiMessage}>
          <section
            className="api-message-modal"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="api-message-title"
            aria-describedby="api-message-content"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="api-message-icon" aria-hidden="true">!</div>
            <h2 id="api-message-title">Thông báo</h2>
            <p id="api-message-content">{apiMessageModal.message}</p>
            <button type="button" onClick={closeApiMessage} autoFocus>
              Đóng
            </button>
          </section>
        </div>
      )}

      <div className="header">
        <h1>Quản Lý Bill</h1>
      </div>

      <div className="filters-section">
        <div className="filter-group">
          <label>Cửa Hàng:</label>
          {isBakery ? (
            <input
              type="text"
              value={storeSearchTerm}
              readOnly
              disabled
              style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
            />
          ) : (
            <div className="store-dropdown-container" style={{ position: 'relative' }}>
              <input
                type="text"
                value={storeSearchTerm}
                onChange={(e) => {
                  setStoreSearchTerm(e.target.value);
                  setShowStoreDropdown(true);
                }}
                onFocus={() => setShowStoreDropdown(true)}
                placeholder="Tìm kiếm cửa hàng..."
                style={{ width: '100%' }}
              />
              {showStoreDropdown && (
                <>
                  <div 
                    style={{ 
                      position: 'fixed', 
                      top: 0, 
                      left: 0, 
                      right: 0, 
                      bottom: 0, 
                      zIndex: 999 
                    }}
                    onClick={() => setShowStoreDropdown(false)}
                  />
                  <div 
                    className="store-dropdown-list"
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      backgroundColor: 'white',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      maxHeight: '300px',
                      overflowY: 'auto',
                      zIndex: 1000,
                      boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                      marginTop: '4px'
                    }}
                  >
                    {getFilteredStores().length === 0 ? (
                      <div style={{ padding: '10px', textAlign: 'center', color: '#999' }}>
                        Không tìm thấy cửa hàng
                      </div>
                    ) : (
                      getFilteredStores().map(store => (
                        <div
                          key={store.IDCH}
                          onClick={() => selectStore(store)}
                          style={{
                            padding: '10px',
                            cursor: 'pointer',
                            borderBottom: '1px solid #f0f0f0',
                            backgroundColor: selectedStore?.IDCH === store.IDCH ? '#e3f2fd' : 'white'
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = selectedStore?.IDCH === store.IDCH ? '#e3f2fd' : 'white'}
                        >
                          <div style={{ fontWeight: 'bold' }}>{store.TenCuaHang}</div>
                          <div style={{ fontSize: '12px', color: '#666' }}>
                            Mã: {store.IDCH} | {store.MaBravo}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
        <div className="filter-group">
          <label>Từ Ngày:</label>
          <input
            type="date"
            value={filters.TuNgay}
            onChange={(e) => setFilters({ ...filters, TuNgay: e.target.value })}
          />
        </div>
        <div className="filter-group">
          <label>Đến Ngày:</label>
          <input
            type="date"
            value={filters.DenNgay}
            onChange={(e) => setFilters({ ...filters, DenNgay: e.target.value })}
          />
        </div>
        <button className="btn btn-primary" onClick={fetchBills}>
          Tìm Kiếm
        </button>
                <div className="filter-group">
          <label>Tìm Theo Mã HD:</label>
          <div style={{ display: 'flex', gap: '8px' }}>
            <input
              type="number"
              value={soPxSearchInput}
              onChange={(e) => setSoPxSearchInput(e.target.value.replace(/\D/g, ''))}
              placeholder="Nhập số hóa đơn..."
            />
            <button className="btn btn-primary" onClick={handleSoPXSearch}>
              Search
            </button>
          </div>
        </div>
      </div>

      {loading && <div className="loading">Đang tải...</div>}

      <PaginationControls />

      <div className="bills-table">
        <table>
          <thead>
            <tr>
              <th>Mã HD</th>
              <th>Ngày CT</th>
              <th>Mã CH</th>
              <th>Tên Cửa Hàng</th>
              <th>Tổng Tiền</th>
              <th>HTTT</th>
              <th>Ghi Chú</th>
              <th>Thao Tác</th>
            </tr>
          </thead>
          <tbody>
            {bills.length === 0 ? (
              <tr>
                <td colSpan="8" style={{ textAlign: 'center', padding: '40px' }}>
                  Không có dữ liệu
                </td>
              </tr>
            ) : (
              bills.map((bill) => (
                <tr key={bill.IDXuat || bill.SoCT}>
                  <td>{bill.MaPX || bill.IDXuat || bill.SoCT}</td>
                  <td>{new Date(bill.NgayXuat || bill.NgayCT).toLocaleDateString('vi-VN')}</td>
                  <td>{bill.MaCH}</td>
                  <td>{bill.TenCuaHang}</td>
                  <td className="currency">{formatCurrency(bill.TongTien)}</td>
                  <td>{bill.MaHTTT}</td>
                  <td>{bill.GhiChu}</td>
                  <td>
                    <button
                      className="btn btn-info btn-sm"
                      onClick={() => fetchBillDetail(bill.IDXuat || bill.SoCT)}
                    >
                      Chi Tiết
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <PaginationControls />

      {showModal && selectedBill && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Chi Tiết Bill #{selectedBill.SoPX} - {selectedBill.MaPX}</h2>
              <button className="close-btn" onClick={() => setShowModal(false)}>×</button>
            </div>
            
            <div className="modal-body">
              <div className="bill-info">
                <div className="info-row">
                  <label>Ngày:</label>
                  <span>{new Date(selectedBill.NgayXuat).toLocaleDateString('vi-VN')}</span>
                </div>
                <div className="info-row">
                  <label>Cửa Hàng:</label>
                  <span>{selectedBill.TenCuaHang} (Mã: {selectedBill.MaCH})</span>
                </div>
                <div className="info-row">
                  <label>Tổng Tiền:</label>
                  <input
                    type="number"
                    value={selectedBill.TongTien}
                    readOnly
                    disabled
                    style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed', fontWeight: 'bold', color: '#27ae60' }}
                  />
                </div>
                {isAdmin && (
                  <div className="info-row">
                    <label htmlFor="bill-discount-percent">Giảm Giá (%):</label>
                    <input
                      id="bill-discount-percent"
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      value={billForm.GiamPT}
                      disabled={savingBill}
                      onChange={(e) => setBillForm({ ...billForm, GiamPT: e.target.value })}
                    />
                    <small style={{ color: '#666' }}>
                      {`Tiền hàng ${formatCurrency(selectedBill.TienHang)} → sau giảm ${formatCurrency(calcBillTotal(selectedBill.TienHang, billForm.GiamPT, selectedBill.GiamTM))}`}
                    </small>
                  </div>
                )}
                <div className="info-row">
                  <label htmlFor="bill-payment-method">Hình Thức TT:</label>
                  <select
                    id="bill-payment-method"
                    value={billForm.MaHTTT}
                    disabled={savingBill}
                    onChange={handlePaymentMethodChange}
                  >
                    <option value="0">Tiền Mặt</option>
                    <option value="1">Chuyển Khoản</option>
                  </select>
                </div>
                <div className="info-row">
                  <label htmlFor="bill-note">Ghi Chú:</label>
                  <textarea
                    id="bill-note"
                    value={billForm.GhiChu}
                    disabled={savingBill}
                    onChange={(e) => setBillForm({ ...billForm, GhiChu: e.target.value })}
                  />
                </div>
              </div>

              <div className="items-section">
                <div className="section-header">
                  <h3>Danh Sách Sản Phẩm ({selectedBill.TotalItems || selectedBill.items?.length || 0})</h3>
                  {isAdmin && (
                    <button className="btn btn-success btn-sm" onClick={openAddItemModal}>
                      + Thêm Sản Phẩm
                    </button>
                  )}
                </div>
                
                <table className="items-table">
                  <thead>
                    <tr>
                      <th>Mã SP</th>
                      <th>Tên SP</th>
                      <th>Số Lượng</th>
                      <th>Giá Bán</th>
                      <th>Thành Tiền</th>
                      {isAdmin && <th>Thao Tác</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {selectedBill.items?.length === 0 ? (
                      <tr>
                        <td colSpan={isAdmin ? 6 : 5} style={{ textAlign: 'center', padding: '20px' }}>
                          Chưa có sản phẩm
                        </td>
                      </tr>
                    ) : (
                      selectedBill.items?.map((item) => (
                        <tr key={item.IDXuatCT}>
                          <td>{item.MaSP}</td>
                          <td>{item.TenSP}</td>
                          <td>{item.SoLuong}</td>
                          <td className="currency">{formatCurrency(item.GiaBan)}</td>
                          <td className="currency">{formatCurrency(item.ThanhTien)}</td>
                          {isAdmin && (
                            <td>
                              <button
                                className="btn btn-warning btn-sm"
                                onClick={() => openEditItemModal(item)}
                              >
                                Sửa
                              </button>
                              <button
                                className="btn btn-danger btn-sm"
                                onClick={() => deleteItem(item.IDXuatCT)}
                              >
                                Xóa
                              </button>
                            </td>
                          )}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="modal-footer">
              {isAdmin && (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSyncTax}
                  disabled={taxSyncStatus === 'loading' || loading || savingBill || !selectedBill.IDXuat}
                  aria-live="polite"
                  aria-busy={taxSyncStatus === 'loading'}
                >
                  {taxSyncStatus === 'loading' ? 'Đang đồng bộ'
                    : taxSyncStatus === 'error' ? 'Đồng bộ thất bại'
                    : taxSyncStatus === 'success' ? 'Đồng bộ thành công'
                    : 'Đồng bộ'}
                </button>
              )}
              <button
                className="btn btn-primary"
                onClick={handleUpdateBill}
                disabled={savingBill || loading || !hasBillChanges}
              >
                {savingBill ? 'Đang cập nhật...' : 'Cập nhật'}
              </button>
              {isAdmin && (
                <button className="btn btn-danger" onClick={() => deleteBill(selectedBill.IDXuat)}>
                  Xóa Bill
                </button>
              )}
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>
                Đóng
              </button>
            </div>
          </div>
        </div>
      )}

      {showItemModal && isAdmin && (
        <div className="modal-overlay" onClick={() => setShowItemModal(false)}>
          <div className="modal-content modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingItem ? 'Sửa Sản Phẩm' : 'Thêm Sản Phẩm'}</h2>
              <button className="close-btn" onClick={() => setShowItemModal(false)}>×</button>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                {!editingItem && (
                  <>
                    <label>Tìm kiếm sản phẩm:</label>
                    <input
                      type="text"
                      placeholder="Nhập tên, mã SP hoặc mã vạch..."
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        searchProducts(e.target.value);
                      }}
                      style={{ marginBottom: '10px' }}
                    />

                    {searchLoading && (
                      <div style={{ textAlign: 'center', padding: '10px', color: '#3498db' }}>
                        Đang tìm kiếm...
                      </div>
                    )}

                    {searchResults.length > 0 && (
                      <div className="search-results-grid">
                        {searchResults.map((product) => (
                          <div
                            key={product.MaSP}
                            className="product-card"
                            onClick={() => selectProduct(product)}
                          >
                            <div className="product-code">{product.MaSP}</div>
                            <div className="product-name">{product.Ten_SP}</div>
                            <div className="product-price">{formatCurrency(product.DonGia)}</div>
                            <div className="product-unit">{product.DVT_SP}</div>
                          </div>
                        ))}
                      </div>
                    )}

                    {itemForm.MaSP && (
                      <>
                        <div className="selected-product">
                          <strong>Sản phẩm đã chọn:</strong>
                          <div style={{ marginTop: '5px', padding: '10px', background: '#e8f5e9', borderRadius: '4px' }}>
                            <div><strong>{itemForm.TenSP}</strong></div>
                            <div>Mã: {itemForm.MaSP} | ĐVT: {itemForm.DVT_XCT}</div>
                          </div>
                        </div>

                        <label style={{ marginTop: '15px' }}>Giá Bán:</label>
                        <input
                          type="number"
                          value={itemForm.GiaBan || 0}
                          onChange={(e) => {
                            const giaBan = parseFloat(e.target.value);
                            const soLuong = itemForm.SoLuong || 1;
                            setItemForm({
                              ...itemForm,
                              GiaBan: giaBan,
                              ThanhTien: giaBan * soLuong
                            });
                          }}
                        />

                        <label>Số Lượng:</label>
                        <input
                          type="number"
                          value={itemForm.SoLuong || 1}
                          onChange={(e) => {
                            const soLuong = parseInt(e.target.value);
                            const giaBan = itemForm.GiaBan || 0;
                            setItemForm({
                              ...itemForm,
                              SoLuong: soLuong,
                              ThanhTien: giaBan * soLuong
                            });
                          }}
                        />
                        
                        <label>Thành Tiền:</label>
                        <input
                          type="number"
                          value={itemForm.ThanhTien || 0}
                          readOnly
                          style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed', fontWeight: 'bold', color: '#27ae60' }}
                        />
                      </>
                    )}
                  </>
                )}
                
                {editingItem && (
                  <>
                    <label>Mã SP:</label>
                    <input
                      type="text"
                      value={editingItem.MaSP}
                      readOnly
                      disabled
                      style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                    />

                    <label>Tên SP:</label>
                    <input
                      type="text"
                      value={editingItem.TenSP}
                      readOnly
                      disabled
                      style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                    />

                    <label>Giá Bán:</label>
                    <input
                      type="number"
                      value={itemForm.GiaBan || 0}
                      readOnly
                      disabled
                      style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                    />

                    <label>Số Lượng:</label>
                    <input
                      type="number"
                      value={itemForm.SoLuong || 1}
                      onChange={(e) => {
                        const soLuong = parseInt(e.target.value);
                        const giaBan = itemForm.GiaBan || 0;
                        setItemForm({
                          ...itemForm,
                          SoLuong: soLuong,
                          ThanhTien: giaBan * soLuong
                        });
                      }}
                    />
                    
                    <label>Thành Tiền:</label>
                    <input
                      type="number"
                      value={itemForm.ThanhTien || 0}
                      readOnly
                      style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed', fontWeight: 'bold', color: '#27ae60' }}
                    />
                  </>
                )}
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="btn btn-primary"
                onClick={() => editingItem ? updateBillItem(editingItem.IDXuatCT) : addItemToBill()}
                disabled={!editingItem && !itemForm.MaSP}
              >
                {editingItem ? 'Cập Nhật' : 'Thêm'}
              </button>
              <button className="btn btn-secondary" onClick={() => setShowItemModal(false)}>
                Hủy
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageBill;
